/**
 * checklistOptions — the Checklist dropdown's options on a job form, with
 * the checklist actually attached to the job always among them.
 *
 * The dropdown lists the templates this person may assign: active versions
 * only, filtered by visibility. A job keeps the version it was given, so
 * once that template is edited in the CMS (a new version, the old one
 * retired) — or simply isn't visible to this person — the attached
 * checklist dropped out of the list and the dropdown read "Select
 * checklist...", as if nothing were attached. Picking the template again to
 * "fix" that replaced the checklist and wiped the job's progress.
 */
import type { ChecklistTemplate } from '../services/checklistTemplateService';

/** What a job form knows about the checklist attached to the job it edits. */
export interface AttachedChecklist {
	/** Source template id — the dropdown value that means "keep this one". */
	id: string;
	title: string;
	/** False once the template was edited in the CMS and this version retired. */
	isActive: boolean;
	/** Items holding work a replacement would remove, counted by the server. */
	progressCount: number;
}

/** The two fields the dropdown reads. */
export type ChecklistOption = Pick<ChecklistTemplate, 'id' | 'title'>;

/** The assignable templates, with the attached checklist first when it isn't one of them. */
export function withAttachedChecklist(assignable: ChecklistOption[], attached: AttachedChecklist | null): ChecklistOption[] {
	if (!attached || assignable.some((option) => String(option.id) === attached.id)) return assignable;
	const note = attached.isActive ? 'in use' : 'older version, in use';
	return [{ id: attached.id, title: `${attached.title} (${note})` }, ...assignable];
}
